// composables/contractService.js
import { readContract, writeContract, waitForTransactionReceipt, getBalance, watchChainId } from '@wagmi/core';
import Swal from "sweetalert2";
import { config } from './walletConfig';
import { abi } from './pcd_abi';                   // 同上
import addresses from './addresses';           // 同上
import utils from '@/composables/utils';       // 包含 convertToWei 與 convertFromWei
import { JsonRpcProvider, Contract, formatUnits } from 'ethers';
import { ethers } from 'ethers';
import { queryRecentContractEvents } from './ethService';

const { convertToWei, convertFromWei } = utils;
const USDT_DECIMALS = 6;

/**
 * 讀取合約中餘額（balanceOf）的方法
 * @param {String} currentAccount 用户钱包地址
 * @returns {Promise<String>} 返回转换后余额
 */
export async function fetchBalanceOf(currentAccount) {
  if (!currentAccount) throw new Error('未連接錢包');
  try {
    const balance = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'balanceOf',
      args: [currentAccount]
    });
    console.log('fetchBalanceOf',balance);
    return convertFromWei(balance, 18, 2);
  } catch (error) {
    console.error("查詢質押餘額時發生錯誤:", error);
    throw error;
  }
}

/**
 * 讀取合約中質押份額（balanceOfShares）
 */
export async function fetchBalanceOfShares(currentAccount) {
  if (!currentAccount) throw new Error('未連接錢包');
  try {
    const balance = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'balanceOfShares',
      args: [currentAccount]
    });
    return convertFromWei(balance, 18, 2);
  } catch (error) {
    console.error("查詢質押份額時發生錯誤:", error);
    throw error;
  }
}

/**
 * 執行 USDT 授權操作
 * @param {Number} amount 授權數量（普通單位）
 */
export async function approveUsdt(amount) {
  if (amount <= 0) {
    throw new Error('請輸入需要投資的數量');
  }
  const usdtAmount = convertToWei(amount, USDT_DECIMALS);
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.usdtContractaddress,
    functionName: 'approve',
    args: [addresses.pcdContractAddress, usdtAmount],
  });
  if (!result) throw new Error('USDT 授權交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 監控交易狀態
 * @param {String} txHash 交易哈希
 */
export async function monitorTransaction(txHash) {
  try {
    const receipt = await waitForTransactionReceipt(config, { hash: txHash });
    if (receipt && receipt.status === "success") {
      console.log("交易成功:", receipt);
      // 可在此處調用刷新數據的接口
    } else {
      console.log("交易失敗:", receipt);
    }
  } catch (error) {
    console.error("監控交易時發生錯誤:", error);
    throw error;
  }
}

/**
 * 其他合約調用方法，比如投資、領取分紅、存入、提現、獲取授權、查詢餘額等，
 * 根據具體業務需求你可以繼續在此文件中添加其它函數。
 */

// 示例：投資操作
export async function investInPCD(currentAccount, amount) {
  const usdtAmount = convertToWei(amount, USDT_DECIMALS);
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'invest',
    args: [usdtAmount.toString()],
  });
  if (!result) throw new Error('投資交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 獲取用戶USDT餘額
 * @param {String} currentAccount 用户钱包地址
 * @returns {Promise<String>} 返回格式化後的USDT餘額
 */
export async function getUsdtBalance(currentAccount) {
  if (!currentAccount) throw new Error('未連接錢包');
  try {
    const balance = await readContract(config, {
      abi: [
        {
          name: 'balanceOf',
          type: 'function',
          stateMutability: 'view',
          inputs: [{ name: 'account', type: 'address' }],
          outputs: [{ name: '', type: 'uint256' }],
        }
      ],
      address: addresses.usdtAddress,
      functionName: 'balanceOf',
      args: [currentAccount]
    });
    return convertFromWei(balance, USDT_DECIMALS, 2);
  } catch (error) {
    console.error("獲取USDT餘額時發生錯誤:", error);
    throw error;
  }
}

/**
 * 獲取用戶ETH餘額
 * @param {String} currentAccount 用户钱包地址
 * @returns {Promise<String>} 返回格式化後的ETH餘額
 */
export async function getEthBalance(currentAccount) {
  if (!currentAccount) throw new Error('未連接錢包');
  try {
    const balance = await getBalance(config, {
      address: currentAccount,
    });
    return convertFromWei(balance.value, 18, 4);
  } catch (error) {
    console.error("獲取ETH餘額時發生錯誤:", error);
    throw error;
  }
}

/**
 * 提取分紅
 * @param {String} currentAccount 用户钱包地址
 * @returns {Promise<void>}
 */
export async function withdrawDividend() {
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'withdrawDividend',
    args: []
  });
  if (!result) throw new Error('提取分紅交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 獲取創始人地址
 * @returns {Promise<Object>} 返回創始人地址
 */
export async function getFounders() {
  try {
    const founders = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'getFounders',
      args: []
    });
    return {
      founder1: founders[0],
      founder2: founders[1]
    };
  } catch (error) {
    console.error("獲取創始人地址時發生錯誤:", error);
    throw error;
  }
}

/**
 * 設置創始人地址
 * @param {String} founder1 創始人1地址
 * @param {String} founder2 創始人2地址
 */
export async function setFounders(founder1, founder2) {
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'setFounders',
    args: [founder1, founder2]
  });
  if (!result) throw new Error('設置創始人交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 提取ETH
 * @param {Number} amount 提取數量
 */
export async function extractETH(amount) {
  const weiAmount = convertToWei(amount, 18);
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'extractETH',
    args: [weiAmount]
  });
  if (!result) throw new Error('提取ETH交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 獲取分紅總份額
 * @returns {Promise<String>} 返回格式化後的分紅總份額
 */
export async function getTotalSharesForDividends() {
  try {
    const shares = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'getTotalSharesForDividends',
      args: []
    });
    return convertFromWei(shares, 18, 2);
  } catch (error) {
    console.error("獲取分紅總份額時發生錯誤:", error);
    throw error;
  }
}

/**
 * 接收分紅
 * @param {Number} amount 分紅數量
 */
export async function receiveDividends(amount) {
  const weiAmount = convertToWei(amount, 6);
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'receiveDividends',
    args: [weiAmount]
  });
  if (!result) throw new Error('分紅存入交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 分配未分配的分紅
 */
export async function distributeUndistributedDividends() {
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'distributeUndistributedDividends',
    args: []
  });
  if (!result) throw new Error('分紅分配交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 獲取用戶的分紅
 * @param {String} currentAccount 用户钱包地址
 * @returns {Promise<String>} 返回格式化後的分紅
 */
export async function getDividendOf(currentAccount) {
  if (!currentAccount) throw new Error('未連接錢包');
  try {
    const dividend = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'dividendOf',
      args: [currentAccount]
    });
    return convertFromWei(dividend, 18, 4);
  } catch (error) {
    console.error("獲取用戶分紅時發生錯誤:", error);
    throw error;
  }
}

/**
 * 獲取用戶累計分紅
 * @param {String} currentAccount 用户钱包地址
 * @returns {Promise<String>} 返回格式化後的累計分紅
 */
export async function getAccumulativeDividendOf(currentAccount) {
  if (!currentAccount) throw new Error('未連接錢包');
  try {
    const dividend = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'accumulativeDividendOf',
      args: [currentAccount]
    });
    return convertFromWei(dividend, 6, 4);
  } catch (error) {
    console.error("獲取用戶累計分紅時發生錯誤:", error);
    throw error;
  }
}

/**
 * 領取分紅
 */
export async function claimDividends() {
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'claimDividends',
    args: []
  });
  if (!result) throw new Error('交易未發送');
  await monitorTransaction(result);
  // 不在這裡彈窗，讓外部處理
}

/**
 * 存入代幣
 * @param {Number} amount 存入數量
 */
export async function deposit(amount) {
  const weiAmount = convertToWei(amount, 18);
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'deposit',
    args: [weiAmount]
  });
  if (!result) throw new Error('存入代幣交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 提取代幣
 * @param {Number} amount 提取數量
 */
export async function withdraw(amount) {
  const weiAmount = convertToWei(amount, 18);
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'withdraw',
    args: [weiAmount]
  });
  if (!result) throw new Error('提取代幣交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 鎖定地址
 * @param {String} account 要鎖定的地址
 */
export async function lockAddress(account) {
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'lockAddress',
    args: [account]
  });
  if (!result) throw new Error('鎖定地址交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 解鎖地址
 * @param {String} account 要解鎖的地址
 */
export async function unlockAddress(account) {
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'unlockAddress',
    args: [account]
  });
  if (!result) throw new Error('解鎖地址交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 檢查地址是否被鎖定
 * @param {String} account 要檢查的地址
 * @returns {Promise<Boolean>} 返回是否被鎖定
 */
export async function isLocked(account) {
  if (!account) throw new Error('未輸入地址');
  try {
    return await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'isLocked',
      args: [account]
    });
  } catch (error) {
    console.error("檢查地址鎖定狀態時發生錯誤:", error);
    throw error;
  }
}

/**
 * 獲取用戶可用餘額
 * @param {String} currentAccount 用户钱包地址
 * @returns {Promise<String>} 返回格式化後的可用餘額
 */
export async function fetchBalanceOfAvailable(currentAccount) {
  if (!currentAccount) throw new Error('未連接錢包');
  try {
    const balance = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'balanceOfAvailable',
      args: [currentAccount]
    });
    return convertFromWei(balance, 18, 2);
  } catch (error) {
    console.error("查詢可用餘額時發生錯誤:", error);
    throw error;
  }
}

/**
 * 獲取PCD Token價格（USDT計價）
 * @returns {Promise<string>} 返回格式化後的PCD價格
 */
export async function getPcdTokenPrice() {
  try {
    const price = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'tokenPrice',
      args: []
    });
    // 假設PCD價格與USDT同精度（6位），如有不同請調整
    return convertFromWei(price, 6, 6);
  } catch (error) {
    console.error('獲取PCD價格時發生錯誤:', error);
    throw error;
  }
}

/**
 * 查詢合約的冷卻時間（claimWait，單位：秒）
 * 用於判斷距離上次領取後還需等待多久才能再次領取
 * @returns {Promise<number>} 返回冷卻秒數
 */
export async function getClaimWait() {
  try {
    const wait = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'claimWait',
      args: []
    });
    return Number(wait);
  } catch (error) {
    console.error('查詢 claimWait 時發生錯誤:', error);
    throw error;
  }
}

/**
 * 查詢某地址的上次領取分紅時間戳（lastClaimTimes，單位：秒）
 * 用於計算距離下次可領取還需等待多久
 * @param {string} address 用戶地址
 * @returns {Promise<number>} 返回時間戳（秒）
 */
export async function getLastClaimTime(address) {
  if (!address) throw new Error('未連接錢包');
  try {
    const ts = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'lastClaimTimes',
      args: [address]
    });
    return Number(ts);
  } catch (error) {
    console.error('查詢 lastClaimTimes 時發生錯誤:', error);
    throw error;
  }
}

/**
 * 設定私募授權地址
 * @param {string} investor 私募地址
 * @param {boolean} enabled 是否啟用
 * @returns {Promise<any>}
 */
export async function setPrivateInvestor(investor, enabled) {
  if (!investor) throw new Error('請輸入私募地址');
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'setPrivateInvestor',
    args: [investor, enabled]
  });
  if (!result) throw new Error('設置私募授權地址交易未發送');
  await monitorTransaction(result);
  return result;
}


/**
 * 調用私募投資（privateInvest）方法
 * @param {number|string} usdtAmount USDT 數量（6位小數）
 * @returns {Promise<any>}
 */
export async function privateInvest(usdtAmount) {
  if (!usdtAmount || isNaN(usdtAmount)) throw new Error('請輸入有效的 USDT 數量');
  // 轉換為 6 位小數的 wei 格式
  const amountWei = convertToWei(usdtAmount, 6);
  console.log('amountWei', amountWei);
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'privateInvest',
    args: [amountWei]
  });
  if (!result) throw new Error('私募投資交易未發送');
  await monitorTransaction(result);
  return result;
}


/**
 * 查詢最近N條PCD分紅領取記錄（API 版）
 * @param {string} userAddress 用戶地址
 * @param {number} count 條數，默認5
 * @returns {Promise<Array<{amount:string,txHash:string,blockNumber:number,time:string}>>}
 */
export async function fetchDividendRecords(userAddress, count = 5) {
  if (!userAddress) return [];
  try {
    // DividendWithdrawn(address to, uint256 weiAmount)
    // topic0 hash:
    const topic0 = '0xee503bee2bb6a87e57bc57db795f98137327401a0e7b7ce42e37926cc1a9ca4d'; // 請替換為真實hash
    const contractAddress = addresses.pcdContractAddress;
    const logs = await queryRecentContractEvents({
      contractAddress,
      topic0,
      address: userAddress,
      page: 1,
      offset: 100, // 获取更多记录以确保能过滤出足够的当前地址记录
      chainId: 137, // Polygon主網
      sort: 'desc' // 按時間倒序
    }, count);

    console.log('fetchDividendRecords', logs);
    // 解析log數據並過濾當前地址的記錄
    const records = logs
      .filter(log => {
        // 確保是當前地址的記錄
        const topics = log.topics || [];
        // topics[1] 是 to 地址
        const toAddress = topics[1] ? '0x' + topics[1].slice(26) : '';
        return toAddress.toLowerCase() === userAddress.toLowerCase();
      })
      .slice(0, count) // 只取前 count 條
      .map(log => {
        const data = log.data.startsWith('0x') ? log.data.slice(2) : log.data;
        const amountHex = '0x' + data.slice(0, 64);
        const amountRaw = BigInt(amountHex);
        const amount = Number(formatUnits(amountRaw, 6)).toFixed(2);
        let time = null;
        if (log.timeStamp) {
          const timestamp = parseInt(log.timeStamp, 16) * 1000;
          const date = new Date(timestamp);
          const pad = n => n.toString().padStart(2, '0');
          time = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        }
        return {
          amount,
          txHash: log.transactionHash,
          blockNumber: Number(log.blockNumber),
          time
        };
      });

    // 按時間倒序排序
    return records.sort((a, b) => {
      if (!a.time || !b.time) return 0;
      return new Date(b.time) - new Date(a.time);
    });

  } catch (e) {
    console.error('[fetchDividendRecords-API] error:', e);
    throw e;
  }
}

/**
 * 提取PCD合約USDT（owner專用）
 * @param {number|string} amount 提取數量（USDT，普通單位）
 */
export async function extractDividends(amount) {
  const usdtAmount = convertToWei(amount, 6);
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'extractDividends',
    args: [usdtAmount]
  });
  if (!result) throw new Error('USDT提取交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 獲取用戶已領取的PCD數量
 * @param {string} address 用戶地址
 * @returns {Promise<number>} 已領取的PCD數量
 */
export async function getClaimedPCD(address) {
  try {
    const result = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'getClaimedPCD',
      args: [address]
    });
    return convertFromWei(result, 18, 2);
  } catch (e) {
    console.error('[getClaimedPCD] error:', e);
    return 0;
  }
}

/**
 * 更新最小松樹數量（owner專用）
 * @param {number} newMinPine 新的最小松樹數量
 * @returns {Promise<string>} 交易哈希
 */
export async function updateMinPineForClaim(newMinPine) {
  if (!newMinPine || newMinPine < 1) {
    throw new Error('最小松樹數量必須大於0');
  }
  
  const result = await writeContract(config, {
    chainId: 137,
    abi,
    address: addresses.pcdContractAddress,
    functionName: 'setMinPineForClaim',
    args: [newMinPine]
  });
  
  if (!result) throw new Error('更新最小松樹數量交易未發送');
  await monitorTransaction(result);
  return result;
}

/**
 * 用戶領取PCD的功能
 * @param {string} address 用戶地址
 * @returns {Promise<string>} 交易哈希
 */
export async function claimPCD(address) {
  if (!address) {
    throw new Error('請提供有效的用戶地址');
  }
  try {
    const result = await writeContract(config, {
      chainId: 137,
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'claimPCD',
      args: [],
      account: address
    });
    if (!result) throw new Error('PCD領取交易未發送');
    await monitorTransaction(result);
    return result;
  } catch (e) {
    console.error('[claimPCD] error:', e);
    throw e;
  }
}

/**
 * 查詢用戶可領取的PCD數量
 * @param {string} address 用戶地址
 * @returns {Promise<number>} 可領取的PCD數量
 */
export async function getClaimablePCD(address) {
  if (!address) return 0;
  try {
    const result = await readContract(config, {
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'getClaimablePCD',
      args: [address]
    });
    return convertFromWei(result, 18, 2);
  } catch (e) {
    console.error('[getClaimablePCD] error:', e);
    return 0;
  }
}


/**
 * 調用 mintPCD 鑄造PCD代幣
 * @param {string} to 接收地址
 * @param {number|string} amount 鑄造數量（單位：個，不是wei）
 * @returns {Promise<string>} 交易哈希
 */
export async function mintPCD(to, amount) {
  if (!to) throw new Error('請提供接收地址');
  if (!amount || isNaN(amount) || Number(amount) <= 0) throw new Error('請輸入有效的鑄造數量');
  try {
    // amount 需為整數，且合約內部會自動轉換為18位小數
    const result = await writeContract(config, {
      chainId: 137,
      abi,
      address: addresses.pcdContractAddress,
      functionName: 'mintPCD',
      args: [to, Number(amount)]
    });
    if (!result) throw new Error('mintPCD 交易未發送');
    await monitorTransaction(result);
    return result;
  } catch (e) {
    console.error('[mintPCD] error:', e);
    throw e;
  }
}
